/**
 * Les instantanés d'une affaire — ce que les missions observent.
 *
 * C'est l'écran qui rend visible la garantie du produit : **une analyse lit un
 * état figé du dossier**. Le brouillon accueille ce qui arrive, l'actif est ce
 * que la prochaine mission prendra, les dépassés se souviennent.
 *
 * Deux refus s'affichent tels quels plutôt que d'être masqués : on n'active
 * pas un brouillon — les missions liraient un dossier à moitié converti — et
 * on n'active pas pendant qu'une mission tourne. Le message du portail dit
 * combien de missions on attend ; le répéter ici l'aurait fait diverger.
 *
 * ## Ce qui manquait, et qui faisait tout l'intérêt de l'onglet
 *
 * Le registre était juste et **ne servait à rien**. Trois manques :
 *
 * - **On ne voyait pas ce qu'un instantané contient.** « 24 » sans savoir
 *   lesquelles — alors que la question à laquelle cet onglet répond est
 *   précisément « sur quoi ce métré a-t-il été calculé ? ». Le point d'accès
 *   existait et n'était appelé par personne.
 * - **La comparaison de deux générations était inatteignable.** C'est pourtant
 *   la chose la plus utile ici : ce qui a changé depuis le chiffrage de juin,
 *   et ce que cela remet en cause. Elle rend même ses conséquences, que
 *   personne ne lisait.
 * - **« Préparer » était un clic à l'aveugle.** Rien ne disait ce qui allait
 *   être fait, ni combien de temps, ni qu'une DPGF absente n'empêche rien
 *   quand un CCTP illisible bloque tout.
 *
 * **Ce qui est annoncé avant « Préparer » vient des règles, non d'un texte.**
 * La conversion DWG est requise dès qu'il y a un plan, l'index CCTP dès qu'il
 * y a une pièce écrite, la lecture DPGF jamais. Ces trois règles sont dans
 * `imports/preparation.py` ; l'écran les applique à la composition réelle du
 * brouillon plutôt que d'afficher une promesse générale.
 */
import { useState } from "react";

import { api, ErreurApi, type Page } from "../../api/client";
import {
  CATEGORIES,
  type Instantane,
  type RapportPreparation,
  type Travail,
} from "../../api/types";
import { Avis } from "../../composants/avis";
import { useModale } from "../../composants/clavier";
import { Chargement, Echec, Vide } from "../../composants/etats";
import { Copier } from "./Chat";
import { Etat } from "../../composants/etiquettes";
import { poids, quand } from "../../composants/formats";
import {
  useDonnees,
  useRelance,
  useSuivreLaFile,
  type Veille,
} from "../../composants/donnees";

interface PieceDeLot {
  revision_id: string;
  document_id: string;
  categorie: string;
  nom: string;
  sha256: string;
  octets: number;
}

interface Ecart {
  document_id: string;
  nom: string;
  categorie: string;
}

interface Modifie extends Ecart {
  avant: { sha256: string; octets: number };
  apres: { sha256: string; octets: number };
}

interface Comparaison {
  ajoutes: PieceDeLot[];
  retires: PieceDeLot[];
  modifies: Modifie[];
  identique: boolean;
  consequences: string[];
}

/** Le nom métier d'une catégorie. Un écran destiné au métreur ne lui montre
 *  pas `PLAN_DWG` : c'est le nom de la colonne en base, pas celui du dossier.
 *  Une catégorie inconnue garde son code plutôt que de disparaître. */
function libelleDe(categorie: string): string {
  return CATEGORIES.find((c) => c.clef === categorie)?.libelle ?? categorie;
}

/** Ce que « Préparer » fera, déduit de ce que le brouillon contient.
 *
 * Les trois règles viennent du code de préparation : la conversion est requise
 * **dès qu'il y a un plan DWG**, l'index **dès qu'il y a un CCTP**, et la
 * lecture de la DPGF ne l'est **jamais** — beaucoup de DCE n'en comportent
 * pas, et refuser l'instantané pour cela reviendrait à refuser de chiffrer la
 * moitié des affaires. */
function etapesPrevues(pieces: PieceDeLot[]) {
  const par = (categorie: string) =>
    pieces.filter((p) => p.categorie === categorie).length;
  const plans = par("PLAN_DWG");
  const cctp = par("CCTP");
  const dpgf = par("DPGF");

  return [
    {
      titre: "Conversion des plans",
      detail: plans
        ? `${plans} plan(s) DWG converti(s) en DXF`
        : "aucun plan DWG dans cet instantané",
      requis: plans > 0,
    },
    {
      titre: "Index des pièces écrites",
      detail: cctp
        ? `${cctp} CCTP découpé(s) en articles et indexé(s)`
        : "aucun CCTP dans cet instantané",
      requis: cctp > 0,
    },
    {
      titre: "Lecture de la DPGF",
      detail: dpgf
        ? `${dpgf} DPGF lue(s) et vérifiée(s)`
        : "aucune DPGF dans cet instantané",
      requis: false,
    },
  ];
}

export function Instantanes({
  affaireId, veille,
}: {
  affaireId: string;
  veille: Veille<Travail>;
}) {
  const [agit, poserAgit] = useState<string>("");
  const [erreur, poserErreur] = useState("");
  const [avis, poserAvis] = useState("");
  const [deplie, poserDeplie] = useState<string>("");
  const [aPreparer, poserAPreparer] = useState<Instantane | null>(null);
  const [compare, poserCompare] = useState(false);

  const jeux = useDonnees<Page<Instantane>>(
    () => api.lire<Page<Instantane>>(`/api/v1/affaires/${affaireId}/instantanes`),
    [affaireId],
  );

  async function agir(jeu: Instantane, geste: "preparation" | "activation") {
    poserAgit(jeu.jeu_id);
    poserErreur("");
    poserAvis("");
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/instantanes/${jeu.jeu_id}/${geste}`,
      );
      poserAvis(
        geste === "preparation"
          ? "Préparation mise en file. Suivez-la dans le tiroir des traitements."
          : `L'instantané ${jeu.generation} est désormais actif.`,
      );
      jeux.recharger();
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message : "Le geste a échoué.");
    } finally {
      poserAgit("");
      poserAPreparer(null);
    }
  }

  // **Rouvrir un brouillon sans réimporter.** Un brouillon naissait d'un dépôt
  // de pièce ; rien ne permettait d'en ouvrir un pour RE-PRÉPARER un dossier
  // inchangé après qu'une capacité de préparation devient disponible (un
  // convertisseur DWG, p.ex.). Ce brouillon reprend l'actif ; on le prépare
  // puis on l'active comme les autres.
  async function nouvelleRevision() {
    poserAgit("nouvelle-revision");
    poserErreur("");
    poserAvis("");
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/instantanes/nouvelle-revision`,
      );
      poserAvis(
        "Nouvelle révision ouverte, reprenant l'instantané actif. " +
          "Préparez-la pour reconvertir les plans, puis activez-la.",
      );
      jeux.recharger();
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message : "L'ouverture a échoué.");
    } finally {
      poserAgit("");
    }
  }

  // **C'est l'écran par lequel le défaut s'est vu.** « Préparer » met un
  // travail en file ; l'instantané passe `DRAFT → PROCESSING → READY` sans que
  // personne ne redemande rien, et l'écran affichait « Brouillon » sur un jeu
  // prêt depuis trois minutes.
  useSuivreLaFile(veille.empreinte, jeux.recharger);

  // **Suivre l'exécution en direct.** La veille ne réveille l'écran qu'aux
  // changements d'ÉTAT (DRAFT → PROCESSING → READY) ; pendant qu'une préparation
  // avance étape par étape, on relit l'instantané à cadence rapprochée pour que
  // la liste de contrôle se coche sous les yeux. On s'arrête dès qu'aucune ne
  // tourne (et l'onglet en arrière-plan ne relit rien — cf. useRelance).
  const enPreparation = (jeux.donnees?.donnees ?? [])
    .some((j) => j.etat === "PROCESSING");
  useRelance(jeux.recharger, 2000, enPreparation);

  if (jeux.charge && !jeux.donnees) return <Chargement lignes={3} />;
  if (jeux.erreur) return <Echec erreur={jeux.erreur} reessayer={jeux.recharger} />;

  const liste = jeux.donnees?.donnees ?? [];
  const actif = liste.find((jeu) => jeu.etat === "ACTIVE");

  return (
    <div className="pile">
      {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}
      {avis && <Avis ton="info" cle={avis}>{avis}</Avis>}

      {/* **Ce que la prochaine mission lira, en tête et en clair.** C'est la
          seule question qu'on se pose en ouvrant cet onglet ; la faire lire
          dans une colonne « État » d'un tableau de cinq lignes la noyait. */}
      {actif ? (
        <div className="fanion">
          <div>
            <span className="fanion__titre">
              La prochaine mission lira la génération {actif.generation}
            </span>
            <span className="fanion__detail">
              {actif.pieces} pièce{actif.pieces > 1 ? "s" : ""} figée
              {actif.pieces > 1 ? "s" : ""} · active depuis le{" "}
              {quand(actif.active_le)}
            </span>
          </div>
          <button type="button" className="lien-bouton"
                  onClick={() => poserDeplie(
                    deplie === actif.jeu_id ? "" : actif.jeu_id)}>
            Voir sa composition
          </button>
        </div>
      ) : (
        <Avis ton="attention" cle="aucun-actif" permanent>
          <strong>Aucun instantané actif.</strong> Tant qu'aucune génération
          n'est activée, une analyse n'a rien de figé à lire. Préparez le
          brouillon, puis activez-le.
        </Avis>
      )}

      <Avis ton="info" cle="instantane-fige">
        Une analyse observe l'instantané <strong>actif au moment où elle
        commence</strong>, et le conserve jusqu'à son terme — même si le client
        redépose une pièce entre-temps.
      </Avis>

      {/* **Re-préparer un dossier inchangé.** Le brouillon naît d'ordinaire d'un
          import ; ce bouton en ouvre un reprenant l'actif, pour rejouer la
          préparation (reconvertir les plans) quand une capacité est devenue
          disponible — sans avoir à redéposer une pièce. Caché s'il existe déjà un
          brouillon (on le prépare alors directement). */}
      {actif && !liste.some((jeu) => jeu.etat === "DRAFT") && (
        <div className="rangee">
          <button type="button" className="bouton"
                  onClick={() => void nouvelleRevision()}
                  disabled={agit === "nouvelle-revision"}>
            {agit === "nouvelle-revision" ? "Ouverture…" : "Nouvelle révision"}
          </button>
          <span className="discret">
            Rouvre un brouillon reprenant l'instantané actif — pour re-préparer le
            dossier (reconvertir les plans) sans réimporter de pièce.
          </span>
        </div>
      )}

      {liste.length > 1 && (
        <div className="rangee">
          <button type="button" className="bouton"
                  onClick={() => poserCompare(true)}>
            Comparer deux générations
          </button>
          <span className="discret">
            Ce qui a changé d'un instantané à l'autre, et ce que cela remet en
            cause.
          </span>
        </div>
      )}

      <div className="carte">
        {liste.length === 0 ? (
          <Vide
            titre="Aucun instantané"
            aide="Ajoutez des pièces : elles ouvriront un brouillon automatiquement."
          />
        ) : (
          <div className="defilant">
            <table className="tableau tableau--instantanes">
              <thead>
                <tr>
                  <th style={{ width: 34 }} />
                  <th>Génération</th>
                  <th>État</th>
                  <th>Pièces</th>
                  <th>Activé le</th>
                  <th>Créé le</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {liste.map((jeu) => (
                  <Ligne
                    key={jeu.jeu_id}
                    affaireId={affaireId}
                    jeu={jeu}
                    ouvert={deplie === jeu.jeu_id}
                    basculer={() =>
                      poserDeplie(deplie === jeu.jeu_id ? "" : jeu.jeu_id)}
                    occupe={agit === jeu.jeu_id}
                    preparer={() => poserAPreparer(jeu)}
                    activer={() => void agir(jeu, "activation")}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {aPreparer && (
        <AvantDePreparer
          affaireId={affaireId}
          jeu={aPreparer}
          fermer={() => poserAPreparer(null)}
          lancer={() => void agir(aPreparer, "preparation")}
          occupe={agit === aPreparer.jeu_id}
        />
      )}

      {compare && (
        <Comparer
          affaireId={affaireId}
          jeux={liste}
          fermer={() => poserCompare(false)}
        />
      )}
    </div>
  );
}

/** Une génération, et sa composition quand on la déplie.
 *
 * **La composition n'est demandée qu'à l'ouverture.** Cinq générations de deux
 * cents pièces feraient cinq requêtes et mille lignes pour une question qu'on
 * ne pose que sur l'une d'elles. */
function Ligne({
  affaireId, jeu, ouvert, basculer, occupe, preparer, activer,
}: {
  affaireId: string;
  jeu: Instantane;
  ouvert: boolean;
  basculer: () => void;
  occupe: boolean;
  preparer: () => void;
  activer: () => void;
}) {
  return (
    <>
      <tr className={jeu.etat === "ACTIVE" ? "instantane--actif" : ""}>
        <td>
          <button
            type="button"
            className="action"
            aria-expanded={ouvert}
            aria-label={`${ouvert ? "Replier" : "Déplier"} la composition de la génération ${jeu.generation}`}
            onClick={basculer}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true"
                 className={ouvert ? "groupe__fleche groupe__fleche--ouverte"
                                   : "groupe__fleche"}>
              <path d="m9 6 6 6-6 6" />
            </svg>
          </button>
        </td>
        <td className="mono nombre">{jeu.generation}</td>
        <td>
          <span className="rangee" style={{ gap: "var(--mo-space-2)" }}>
            <Etat genre="instantane" valeur={jeu.etat} />
            {jeu.etat === "PROCESSING" && jeu.rapport?.en_cours && (
              <span className="instantane__encours">
                {jeu.rapport.en_cours}…
              </span>
            )}
            {(jeu.rapport?.avertissements?.length ?? 0) > 0 && (
              <span className="instantane__avert"
                    title="Déplier pour voir le détail">
                ⚠ {jeu.rapport!.avertissements.length}
              </span>
            )}
          </span>
        </td>
        <td className="nombre">{jeu.pieces}</td>
        <td className="discret nombre">{quand(jeu.active_le)}</td>
        <td className="discret nombre">{quand(jeu.cree_le)}</td>
        <td style={{ textAlign: "right" }}>
          <div className="rangee" style={{ justifyContent: "flex-end" }}>
            {(jeu.etat === "DRAFT" || jeu.etat === "FAILED") && (
              <button type="button" className="bouton" disabled={occupe}
                      onClick={preparer}>
                Préparer…
              </button>
            )}
            {jeu.etat === "READY" && (
              <button type="button" className="bouton bouton--primaire"
                      disabled={occupe} onClick={activer}>
                {occupe ? "Activation…" : "Activer"}
              </button>
            )}
          </div>
        </td>
      </tr>
      {jeu.etat === "PROCESSING" && (
        <tr className="instantane__detail">
          <td colSpan={7}>
            <ListeDeControle rapport={jeu.rapport ?? null} />
          </td>
        </tr>
      )}
      {jeu.etat === "FAILED" && (() => {
        // La cause fatale — l'étape requise qui n'a pas abouti — montrée
        // d'emblée : sans elle, l'écran disait « En échec » et n'affichait que
        // des avertissements « non bloquants », sans dire ce qui bloquait.
        const fatales = (jeu.rapport?.etapes ?? [])
          .filter((e) => e.requis && !e.fait);
        return fatales.length > 0 ? (
          <tr className="instantane__detail">
            <td colSpan={7}>
              <div className="avis avis--erreur instantane__rapport">
                <div className="avis__corps pile pile--serree">
                  <strong>Préparation en échec — cause bloquante :</strong>
                  {fatales.map((e, i) => (
                    <span key={i}>{e.nom} : {e.detail}</span>
                  ))}
                  <span className="discret">
                    Corrigez la cause (ou écartez/reclassez la pièce en question
                    dans Documents), puis relancez « Préparer… ».
                  </span>
                </div>
              </div>
            </td>
          </tr>
        ) : null;
      })()}
      {jeu.etat !== "PROCESSING" && ouvert && (
        <tr className="instantane__detail">
          <td colSpan={7}>
            {(jeu.rapport?.avertissements?.length ?? 0) > 0 && (
              <div className="avis avis--attention instantane__rapport">
                <div className="avis__corps pile pile--serree">
                  <strong>
                    Préparation : {jeu.rapport!.avertissements.length} avertissement(s)
                    <span className="discret"> — non bloquant(s)</span>
                  </strong>
                  {jeu.rapport!.avertissements.map((a, i) => (
                    <span key={i} className="mono">• {a}</span>
                  ))}
                </div>
              </div>
            )}
            <Composition affaireId={affaireId} jeuId={jeu.jeu_id} />
          </td>
        </tr>
      )}
    </>
  );
}

/** La liste de contrôle d'une préparation en cours : chaque étape se coche à
 *  mesure. Ce qui est fait porte son bilan (et ses avertissements) ; l'étape en
 *  cours tourne ; le reste attend. */
function ListeDeControle({ rapport }: { rapport: RapportPreparation | null }) {
  const prevues = rapport?.etapes_prevues ?? [];
  const parNom = new Map((rapport?.etapes ?? []).map((e) => [e.nom, e]));
  if (prevues.length === 0) {
    return <span className="discret">Préparation en cours…</span>;
  }
  return (
    <div className="pile pile--serree">
      <strong>Préparation en cours</strong>
      <ul className="prep-liste">
        {prevues.map((nom) => {
          const etape = parNom.get(nom);
          const enCours = rapport?.en_cours === nom;
          const etat = etape
            ? (etape.avertissements.length > 0 ? "warn" : "ok")
            : enCours ? "cours" : "attente";
          return (
            <li key={nom} className={`prep-liste__item prep-liste__item--${etat}`}>
              <span className="prep-liste__puce" aria-hidden="true" />
              <span className="prep-liste__nom">{nom}</span>
              {etape ? (
                <span className="discret prep-liste__detail">{etape.detail}</span>
              ) : enCours ? (
                <span className="discret">en cours…</span>
              ) : (
                <span className="discret">en attente</span>
              )}
              {etape && etape.avertissements.length > 0 && (
                <span className="instantane__avert">⚠ {etape.avertissements.length}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Les pièces figées dans un instantané, par catégorie.
 *
 * **L'empreinte est montrée, tronquée mais présente.** C'est elle qui permet
 * de dire qu'un fichier qu'un client renvoie est bien celui sur lequel le
 * métré a été fait — un nom et une date ne le prouvent pas. */
function Composition({ affaireId, jeuId }: { affaireId: string; jeuId: string }) {
  const composition = useDonnees<{ donnees: PieceDeLot[] }>(
    () => api.lire(`/api/v1/affaires/${affaireId}/instantanes/${jeuId}/composition`),
    [affaireId, jeuId],
  );

  const pieces = composition.donnees?.donnees ?? [];
  if (composition.charge && !composition.donnees) return <Chargement lignes={2} />;
  if (composition.erreur) {
    return <Echec erreur={composition.erreur} reessayer={composition.recharger} />;
  }
  if (pieces.length === 0) {
    return <span className="discret">Cet instantané ne contient aucune pièce.</span>;
  }

  const familles = [...new Set(pieces.map((p) => p.categorie))];

  return (
    <div className="composition">
      {familles.map((famille) => (
        <div key={famille} className="composition__famille">
          <h4>
            {libelleDe(famille)}{" "}
            <span className="discret">
              {pieces.filter((p) => p.categorie === famille).length}
            </span>
          </h4>
          <ul>
            {pieces.filter((p) => p.categorie === famille).map((piece) => (
              <li key={piece.revision_id}>
                <span className="tronque">{piece.nom}</span>
                <span className="discret mono">{poids(piece.octets)}</span>
                <span className="discret mono empreinte" title={piece.sha256}>
                  {piece.sha256.slice(0, 12)}…
                </span>
                {/* Une empreinte qui sert de preuve doit pouvoir se vérifier —
                    se copier, se coller, se comparer — non seulement s'entrevoir
                    au survol. */}
                <Copier texte={piece.sha256} libelle="Copier l'empreinte" />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** Ce que la préparation fera, avant de la lancer.
 *
 * **Un geste qui prend des minutes s'annonce.** Le bouton mettait en file sans
 * rien dire : ni ce qui allait être fait, ni ce qui bloquerait l'activation si
 * cela échouait. */
function AvantDePreparer({
  affaireId, jeu, fermer, lancer, occupe,
}: {
  affaireId: string;
  jeu: Instantane;
  fermer: () => void;
  lancer: () => void;
  occupe: boolean;
}) {
  const composition = useDonnees<{ donnees: PieceDeLot[] }>(
    () => api.lire(`/api/v1/affaires/${affaireId}/instantanes/${jeu.jeu_id}/composition`),
    [affaireId, jeu.jeu_id],
  );

  // Échap ferme — sauf pendant le lancement, où l'on ne quitte pas d'un coup
  // de touche.
  const refModale = useModale(!occupe, fermer);

  const pieces = composition.donnees?.donnees ?? [];
  const etapes = etapesPrevues(pieces);

  return (
    <div ref={refModale} className="voile" role="dialog" aria-modal="true"
         aria-label="Préparer l'instantané">
      <div className="modale" style={{ width: "min(600px, 100%)" }}>
        <div className="modale__tete">
          <h2>Préparer la génération {jeu.generation} ?</h2>
        </div>
        <div className="modale__corps" style={{ padding: "var(--mo-space-5)" }}>
          {composition.charge && !composition.donnees ? (
            <Chargement lignes={3} />
          ) : (
            <div className="pile">
              <p className="discret" style={{ margin: 0 }}>
                La préparation travaille <strong>à côté</strong> : elle ne
                touche pas à l'instantané en service. Si elle échoue, celui-ci
                reste entièrement utilisable.
              </p>
              <ul className="etapes">
                {etapes.map((etape) => (
                  <li key={etape.titre}
                      className={etape.requis ? "etapes--requis" : ""}>
                    <div>
                      <strong>{etape.titre}</strong>{" "}
                      <span className="etapes__marque">
                        {etape.requis ? "requis" : "facultatif"}
                      </span>
                    </div>
                    <span className="discret">{etape.detail}</span>
                  </li>
                ))}
              </ul>
              <Avis ton="info" permanent>
                Le travail part en file : comptez plusieurs minutes sur un
                dossier de plans. Vous pourrez le suivre dans le tiroir
                « Traitements », et l'activation ne sera offerte qu'une fois
                les étapes requises abouties.
              </Avis>
            </div>
          )}
        </div>
        <div className="modale__pied">
          <span style={{ flex: 1 }} />
          <button type="button" className="bouton" onClick={fermer}>
            Annuler
          </button>
          <button type="button" className="bouton bouton--primaire"
                  disabled={occupe} onClick={lancer}>
            {occupe ? "Mise en file…" : "Lancer la préparation"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Comparer deux générations. `M13`.
 *
 * **Trois écarts, et le troisième est le dangereux.** Une pièce ajoutée
 * n'existait pas, une pièce retirée ne sera plus lue — les deux se voient dans
 * une liste de noms. Une pièce *modifiée* porte le même nom et d'autres
 * octets : elle ne se voit qu'à l'empreinte, et c'est celle qui invalide un
 * chiffrage sans prévenir. */
function Comparer({
  affaireId, jeux, fermer,
}: {
  affaireId: string;
  jeux: Instantane[];
  fermer: () => void;
}) {
  const refModale = useModale(true, fermer);

  // Par défaut : l'avant-dernière contre la dernière — la question qu'on pose
  // neuf fois sur dix.
  const [avant, poserAvant] = useState(jeux[1]?.jeu_id ?? "");
  const [apres, poserApres] = useState(jeux[0]?.jeu_id ?? "");

  const ecarts = useDonnees<{ donnees: Comparaison }>(
    () => api.lire(
      `/api/v1/affaires/${affaireId}/instantanes/comparaison`
      + `?avant=${avant}&apres=${apres}`),
    [affaireId, avant, apres],
  );

  const vue = ecarts.donnees?.donnees;
  const nom = (id: string) =>
    `Génération ${jeux.find((j) => j.jeu_id === id)?.generation ?? "?"}`;

  return (
    <div ref={refModale} className="voile" role="dialog" aria-modal="true"
         aria-label="Comparer deux instantanés">
      <div className="modale modale--detail">
        <div className="modale__tete">
          <h2>Comparer deux instantanés</h2>
          <button type="button" className="bouton" onClick={fermer}>Fermer</button>
        </div>

        <div className="picker__outils">
          <label className="champ champ--enligne">
            <span className="discret">Avant</span>
            <select value={avant} onChange={(e) => poserAvant(e.target.value)}
                    aria-label="Instantané de référence">
              {jeux.map((jeu) => (
                <option key={jeu.jeu_id} value={jeu.jeu_id}>{nom(jeu.jeu_id)}</option>
              ))}
            </select>
          </label>
          <span aria-hidden="true">→</span>
          <label className="champ champ--enligne">
            <span className="discret">Après</span>
            <select value={apres} onChange={(e) => poserApres(e.target.value)}
                    aria-label="Instantané comparé">
              {jeux.map((jeu) => (
                <option key={jeu.jeu_id} value={jeu.jeu_id}>{nom(jeu.jeu_id)}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="modale__corps" style={{ padding: "var(--mo-space-5)" }}>
          {ecarts.charge && !ecarts.donnees ? (
            <Chargement lignes={3} />
          ) : ecarts.erreur ? (
            <Echec erreur={ecarts.erreur} reessayer={ecarts.recharger} />
          ) : vue ? (
            <div className="pile">
              {vue.identique ? (
                <Avis ton="info" permanent>
                  Ces deux instantanés contiennent <strong>exactement les mêmes
                  pièces</strong>, aux mêmes versions. Un métré refait sur l'un
                  ou l'autre lira la même chose.
                </Avis>
              ) : (
                <>
                  {/* Les modifiées d'abord : ce sont celles qu'on ne verrait
                      pas autrement. */}
                  <Ecarts titre="Modifiées" ton="attention"
                          aide="Même nom, autres octets. C'est le seul écart
                                qu'une liste de noms ne montre pas."
                          lignes={vue.modifies.map((m) => ({
                            cle: m.document_id,
                            nom: m.nom,
                            detail: `${poids(m.avant.octets)} → ${poids(m.apres.octets)}`,
                          }))} />
                  <Ecarts titre="Ajoutées" ton="succes"
                          aide="Ces pièces n'existaient pas dans l'instantané de
                                référence."
                          lignes={vue.ajoutes.map((p) => ({
                            cle: p.document_id, nom: p.nom,
                            detail: `${libelleDe(p.categorie)} · ${poids(p.octets)}`,
                          }))} />
                  <Ecarts titre="Retirées" ton="erreur"
                          aide="Ces pièces ne seront plus lues par les
                                prochaines missions."
                          lignes={vue.retires.map((p) => ({
                            cle: p.document_id, nom: p.nom,
                            detail: `${libelleDe(p.categorie)} · ${poids(p.octets)}`,
                          }))} />
                </>
              )}

              {/* **Les conséquences viennent du portail**, non d'un texte
                  écrit ici : ce sont des propriétés du geste, et les recopier
                  à l'écran les ferait diverger au premier changement. */}
              <div className="consequences">
                <h4>Ce que cela implique</h4>
                <ul>
                  {vue.consequences.map((phrase) => (
                    <li key={phrase}>{phrase}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Un groupe d'écarts. Absent plutôt que vide : « 0 retirée » n'apprend rien. */
function Ecarts({
  titre, ton, aide, lignes,
}: {
  titre: string;
  ton: "attention" | "succes" | "erreur";
  aide: string;
  lignes: { cle: string; nom: string; detail: string }[];
}) {
  if (lignes.length === 0) return null;

  return (
    <section className={`ecarts ecarts--${ton}`}>
      <h4>
        {titre} <span className="ecarts__compte">{lignes.length}</span>
      </h4>
      <p className="discret">{aide}</p>
      <ul>
        {lignes.map((ligne) => (
          <li key={ligne.cle}>
            <span className="tronque">{ligne.nom}</span>
            <span className="discret mono">{ligne.detail}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
